// Several values picked from a list that typing narrows.
//
// What is picked sits in the box as chips, each with its own remove; the list stays open while
// options are picked and dropped, and marks the ones that are in. Backspace in an empty input
// takes the last chip, as it would take the last character.
//
// The values keep the order they were picked in. One that is no longer on the list is still a
// chip, shown as it is stored, so saving the form does not drop it unasked.

import { useId } from 'react';

import Icon from './Icon.jsx';
import OptionList from './OptionList.jsx';
import { optionFor } from './optionSearch.js';
import useOptionList from './useOptionList.js';

export default function MultiSelect({ label, options, values, onChange, placeholder }) {
  const listId = useId();
  const list = useOptionList(options);

  const toggle = (option) => {
    onChange(values.includes(option.value)
      ? values.filter((value) => value !== option.value)
      : [...values, option.value]);
    // The search has done its work; the list stays for the next pick.
    list.setTyped(null);
  };

  const onKeyDown = (event) => {
    if (event.key === 'Backspace' && !list.typed && values.length > 0) {
      onChange(values.slice(0, -1));
      return;
    }
    list.onKeyDown(toggle)(event);
  };

  return (
    <span className="combobox">
      <span className="multi-select">
        {values.map((value) => {
          const option = optionFor(options, value);
          return (
            <span key={value} className="chip">
              {option.label}
              <button type="button" aria-label={`Remove ${option.label}`} title="Remove"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => onChange(values.filter((kept) => kept !== value))}>
                <Icon name="close" />
              </button>
            </span>
          );
        })}
        <input
          role="combobox"
          aria-label={label}
          aria-expanded={list.open}
          aria-controls={listId}
          aria-autocomplete="list"
          value={list.typed ?? ''}
          placeholder={values.length === 0 ? placeholder : undefined}
          autoComplete="off"
          onChange={(event) => list.type(event.target.value)}
          onClick={list.show}
          onKeyDown={onKeyDown}
          onBlur={list.close}
        />
      </span>
      {list.open && (
        <OptionList id={listId} label={label} options={list.matches} active={list.active}
                    picked={values} multiple onPick={toggle} />
      )}
    </span>
  );
}
